/**
 * The condition shown for a product row (T11-11, CLAUDE.md "raw cards always show Rank A–D"):
 *
 * | product type | shown |
 * |---|---|
 * | `raw_single` | `RankBadge` A–D — **missing rank = red "Rank needed" chip, never empty** |
 * | `graded_single` | "PSA 10" (company + grade) |
 * | `sealed_product` | "未開封 / Sealed" |
 * | `supplies` | nothing |
 *
 * The admin products table (`GET /v1/admin/products`) sends `conditionLabel` = the canonical
 * English condition title of the first item (catalog `app/domain/titles.py`: `Rank B`,
 * `PSA 10`, `Sealed`, `Rank ?` when the rank is missing); a row that also carries `rawRank`
 * (lookup / product detail) uses that directly.
 */
import { isRawRank, type RawRank } from "@tcg/ui";

export type ProductCondition =
  | { kind: "rank"; rank: RawRank; damaged: boolean }
  | { kind: "rank_missing" }
  | { kind: "graded"; label: string }
  | { kind: "sealed" }
  | { kind: "none" };

const RANK_LABEL = /^Rank ([ABCD])$/;

export interface ConditionSource {
  productType: string;
  /** Canonical English label (`Rank B`, `PSA 10`, `Sealed`). */
  conditionLabel?: string | null;
  rawRank?: string | null;
  rawRankDamagedFlag?: boolean | null;
  gradeLabel?: string | null;
  gradingCompany?: string | null;
  grade?: string | null;
}

export function conditionOf(source: ConditionSource): ProductCondition {
  switch (source.productType) {
    case "raw_single": {
      const direct = source.rawRank ?? null;
      const fromLabel = RANK_LABEL.exec((source.conditionLabel ?? "").trim())?.[1] ?? null;
      const rank = direct ?? fromLabel;
      return isRawRank(rank)
        ? { kind: "rank", rank, damaged: rank === "D" && Boolean(source.rawRankDamagedFlag) }
        : { kind: "rank_missing" };
    }
    case "graded_single": {
      const label =
        source.gradeLabel?.trim() ||
        [source.gradingCompany, source.grade].filter(Boolean).join(" ").trim() ||
        (source.conditionLabel ?? "").trim();
      return label ? { kind: "graded", label } : { kind: "none" };
    }
    case "sealed_product":
      return { kind: "sealed" };
    default:
      return { kind: "none" };
  }
}
